/**
 * Module dependencies.
 */

import { AnimationController } from 'src/components/core/animations/animation-controller';
import { Button } from 'src/components/core/buttons/button';
import { FadeInUp } from 'src/components/core/animations/fade-in-up';
import { Faqs } from 'src/components/core/faqs';
import { Grid, SubGrid } from 'src/components/core/layout/grid-content';
import { Heading } from 'src/components/core/heading';
import { Content as PrismicContent } from '@prismicio/client';
import { ReactNode } from 'react';
import { Section } from 'src/components/core/layout/section';
import { routeResolve } from 'src/core/utils/routes';
import { useTranslation } from 'next-i18next';
import iconButton from 'src/assets/svgs/chevron-right.svg';
import iconFaqs from 'src/assets/svgs/faqs.svg';
import styled from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  disclaimer?: ReactNode;
  faqs: PrismicContent.FaqDocumentData[];
};

/**
 * `SeeAllFaqsButton` styled component.
 */

const SeeAllFaqsButton = styled(Button).attrs({
  colorTheme: 'text',
  icon: iconButton,
  reverse: true
})`
  --button-text-color: var(--color-slate400);
`;

/**
 * Export `ClientSupport` component.
 */

export const ClientSupport = ({ disclaimer, faqs }: Props) => {
  const { t } = useTranslation('common');

  if (faqs?.length <= 0) {
    return;
  }

  return (
    <AnimationController>
      <Section style={{ backgroundColor: 'var(--color-cyan50)' }}>
        <Grid>
          <SubGrid>
            <AnimationController>
              <Heading
                asTitle={'h2'}
                icon={iconFaqs}
                label={t('faqs.label')}
                offset
                title={t('faqs.title')}
                titleSize={28}
                titleTheme={'slate900'}
              />
            </AnimationController>
          </SubGrid>
        </Grid>

        <Grid data-has-offset-gutter>
          <SubGrid>
            <Faqs faqs={faqs} isLight triggerInView />

            <FadeInUp style={{ margin: 'var(--space-ms) auto 0', width: 'fit-content' }} triggerInView>
              <SeeAllFaqsButton aria-label={t('actions.seeAllFaqs')} href={routeResolve('faqs')}>
                {t('actions.seeAllFaqs')}
              </SeeAllFaqsButton>
            </FadeInUp>
          </SubGrid>
        </Grid>

        <Grid>
          <SubGrid>{disclaimer && disclaimer}</SubGrid>
        </Grid>
      </Section>
    </AnimationController>
  );
};
